/**
 * WordPress API - WordPress에서 데이터를 가져와 S3에 캐시하는 로직
 *
 * 이 파일은 revalidate 스크립트에서만 사용됩니다.
 * Next.js 빌드/런타임에서는 api.ts의 fetchFromCache를 사용하세요.
 */

import { sendSentryError } from '@lib/sentry';
import { uploadWordpressCacheDataToS3 } from './wordpress-cache';

// Next.js 실행 환경이 아닌 환경에서 사용하기 위해 (ES modules 호이스팅 문제 해결)
const getAuthToken = () => {
  const user = process.env.WORDPRESS_USER;
  const password = process.env.WORDPRESS_APP_PASSWORD;
  if (!user || !password) {
    throw new Error(
      'WORDPRESS_USER와 WORDPRESS_APP_PASSWORD 환경 변수가 설정되어야 합니다.',
    );
  }
  return Buffer.from(`${user}:${password}`).toString('base64');
};

const getApiUrl = () => `${process.env.NEXT_PUBLIC_WORDPRESS_API_URL}/graphql`;

export type RefreshAPIOptions = {
  label: string;
  /** true면 WordPress PHP 필터가 prod_deployed 필터링 적용 (운영 반영 전용) */
  prodFilter?: boolean;
};

/**
 * WordPress GraphQL 조회 (S3 캐시 업로드 없음)
 *
 * 캐시로 저장하지 않는 중간 조회용 (예: editors-pick 캐시 빌드의 소스 포스트 조회)
 */
export async function fetchFromWordpress(
  query = '',
  { variables }: Record<string, any> = {},
  options?: Pick<RefreshAPIOptions, 'prodFilter'>,
) {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Basic ${getAuthToken()}`,
    };

    if (options?.prodFilter) {
      headers['X-Prod-Deployed-Filter'] = '1';
    }

    const apiUrl = getApiUrl();
    if (!apiUrl) {
      throw new Error(
        'NEXT_PUBLIC_WORDPRESS_API_URL 환경 변수가 설정되지 않았습니다.',
      );
    }

    // WPGraphQL Plugin must be enabled
    const res = await fetch(apiUrl, {
      headers,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables,
      }),
      cache: 'no-store',
    });

    if (!res.ok || res.status !== 200) {
      const errorBody = await res.text();
      throw new Error(
        `API 요청 실패: ${res.status} ${res.statusText} ${errorBody}`,
      );
    }

    try {
      const responseTxt = await res.text();
      const json = JSON.parse(responseTxt);

      if (json.errors) {
        throw new Error(json.errors[0].message);
      }

      return json.data;
    } catch (parseError) {
      console.error('❌ JSON 파싱 에러:', parseError);
      throw new Error('❌ JSON 파싱 실패!');
    }
  } catch (error) {
    sendSentryError({
      error,
      extra: { query, variables },
      source: 'wordpress-api-fetch',
    });
    console.error('❌ API 요청 실패:', error);
    throw error;
  }
}

export async function fetchFromWordpressAndCache(
  query = '',
  { variables }: Record<string, any> = {},
  { label, prodFilter }: RefreshAPIOptions,
) {
  const data = await fetchFromWordpress(query, { variables }, { prodFilter });

  try {
    // WORDPRESS DEV 환경 테스트 시 주석 처리
    await uploadWordpressCacheDataToS3(data, label);
  } catch (error) {
    sendSentryError({
      error,
      extra: { query, variables, label },
      source: 'wordpress-api-cache-upload',
    });
    throw error;
  }

  return data;
}

// ============================================
// Pagination Helper for Refresh
// ============================================

interface PaginatedQueryConfig<T> {
  query: string;
  getVariables: (cursor: string | null) => Record<string, any>;
  getPageInfo: (data: any) => {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  getItems: (data: any) => T[];
}

export const fetchAllFromWordpressAndCache = async <T>(
  config: PaginatedQueryConfig<T>,
  options: RefreshAPIOptions,
): Promise<T[]> => {
  let allItems: T[] = [];
  let nextCursor: string | null = null;
  let hasNextPage = true;
  let fromIndex = 1;

  while (hasNextPage) {
    const label = `${options.label}-${fromIndex}`;
    const variables = config.getVariables(nextCursor);
    const postCount = variables.postCount;

    const data = await fetchFromWordpressAndCache(
      config.query,
      { variables },
      { label, prodFilter: options.prodFilter },
    );

    const pageInfo = config.getPageInfo(data);
    const newItems = config.getItems(data);

    allItems = [...allItems, ...newItems];
    nextCursor = pageInfo.endCursor;
    hasNextPage = pageInfo.hasNextPage;
    fromIndex += postCount;
  }

  return allItems;
};
